import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import Dashboard from '../../components/containers/Dashboard/Dashboard';

/**
 * PublicScreenViewer - תצוגה ציבורית של מסך (Business Component)
 * מציג דשבורדים בצורה ציבורית ללא צורך בלוגין
 * 
 * Props:
 * @param {boolean} loading - מצב טעינה
 * @param {string} error - הודעת שגיאה
 * @param {Object} currentDashboard - הדשבורד הנוכחי להצגה
 * @param {string} transition - סוג האנימציה (fade, slide, zoom, flip, none)
 * @param {Object} organization - פרטי הארגון
 * @param {number} currentIndex - אינדקס הדשבורד הנוכחי
 * @param {number} totalDashboards - סך הכל דשבורדים
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} catalog - קטלוג הווידג'טים
 */
const PublicScreenViewer = ({
    loading,
    error,
    currentDashboard,
    transition = 'fade',
    organization,
    currentIndex,
    totalDashboards,
    dispatch,
    catalog
}) => {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [displayDashboard, setDisplayDashboard] = useState(currentDashboard);

    // טיפול במעבר בין דשבורדים עם אנימציה
    useEffect(() => {
        if (!currentDashboard) return;

        // אם זה הדשבורד הראשון, פשוט מציגים אותו
        if (!displayDashboard) {
            setDisplayDashboard(currentDashboard);
            return;
        }

        // אם זה אותו דשבורד, בדוק אם יש עדכון בגירסה
        if (displayDashboard?.dashboardId === currentDashboard?.dashboardId) {
            const displayVersionsCount = displayDashboard.versions?.length || 0;
            const currentVersionsCount = currentDashboard.versions?.length || 0;
            const displayLatestVersionId = displayDashboard.versions?.[displayVersionsCount - 1]?.versionId;
            const currentLatestVersionId = currentDashboard.versions?.[currentVersionsCount - 1]?.versionId;

            // אם יש גירסה חדשה, עדכן ישירות ללא אנימציה
            if (currentLatestVersionId && currentLatestVersionId !== displayLatestVersionId) {
                setDisplayDashboard(currentDashboard);
            }
            return;
        }

        // דשבורד שונה - עשה אנימציה
        setIsTransitioning(true);

        // אחרי האנימציה, מחליפים את הדשבורד ומתחילים אנימציית כניסה
        const timer = setTimeout(() => {
            setDisplayDashboard(currentDashboard);
            setIsTransitioning(false);
        }, 500); // משך האנימציה

        return () => clearTimeout(timer);
    }, [currentDashboard, displayDashboard]);

    // פונקציה לקבלת סגנון האנימציה
    const getTransitionStyle = () => {
        const baseStyle = {
            transition: 'all 0.5s ease-in-out',
            width: '100%',
            height: '100%'
        };

        if (transition === 'none') {
            return baseStyle;
        }

        if (isTransitioning) {
            switch (transition) {
                case 'fade':
                    return { ...baseStyle, opacity: 0 };
                case 'slide':
                    return { ...baseStyle, transform: 'translateX(-100%)' };
                case 'zoom':
                    return { ...baseStyle, transform: 'scale(0.8)', opacity: 0 };
                case 'flip':
                    return { ...baseStyle, transform: 'rotateY(90deg)', opacity: 0 };
                default:
                    return { ...baseStyle, opacity: 0 };
            }
        }

        return { ...baseStyle, opacity: 1, transform: 'none' };
    };


    // Loading state
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    bgcolor: '#f5f5f5'
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2, color: '#666' }}>
                        טוען מסך...
                    </Typography>
                    {totalDashboards > 0 && (
                        <Typography variant="body2" sx={{ mt: 1, color: '#999' }}>
                            לוח {currentIndex + 1} מתוך {totalDashboards}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    bgcolor: '#f5f5f5',
                    p: 4
                }}
            >
                <Alert severity="error" sx={{ maxWidth: 600 }}>
                    <Typography variant="h6" gutterBottom>
                        שגיאה בטעינת המסך
                    </Typography>
                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Alert>
            </Box>
        );
    }

    // Dashboard view
    if (!displayDashboard) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                direction: 'ltr',
                width: '100%',
                height: '100vh',
                overflow: 'hidden',
                perspective: '1000px' // לאנימציית flip
            }}
        >
            <Box sx={getTransitionStyle()}>
                <Dashboard
                    onLogout={() => { }}
                    dispatch={dispatch}
                    hasUnsavedChanges={false}
                    isSaving={false}
                    isEditMode={false}
                    dashboard={displayDashboard}
                    organization={organization}
                    catalog={catalog}
                />
            </Box>
        </Box>
    );
};

export default PublicScreenViewer;
